import React, { useEffect, useRef } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';

interface MermaidDiagramProps {
  diagram: string;
  title?: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ diagram, title = "Architecture Diagram" }) => {
  const mermaidRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!diagram || !mermaidRef.current) return;

      try {
        setIsLoading(true);
        setError(null);

        // Dynamically import mermaid with proper error handling
        const mermaidModule = await import('mermaid');
        const mermaid = mermaidModule.default;
        
        // Initialize mermaid
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true
          },
          architecture: {
            useMaxWidth: true
          }
        });

        // Clear previous content
        mermaidRef.current.innerHTML = '';

        // Generate unique ID for this diagram
        const diagramId = `mermaid-diagram-${Date.now()}`;

        // Render the diagram
        const { svg } = await mermaid.render(diagramId, diagram);
        
        if (mermaidRef.current) {
          mermaidRef.current.innerHTML = svg;
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        const errMsg = err instanceof Error ? err.message : String(err);
        setError(`Failed to render architecture diagram: ${errMsg}`);
        setIsLoading(false);
      }
    };

    renderDiagram();
  }, [diagram]);

  if (!diagram) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography color="text.secondary">
          No diagram available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            Rendering architecture diagram...
          </Typography>
        </Box>
      )}

      {error && (
        <Box py={4}>
          <Typography color="error" variant="body1" textAlign="center">
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} textAlign="center">
            The raw diagram source is shown below. You can paste it into <a href="https://mermaid.live" target="_blank" rel="noopener noreferrer">mermaid.live</a> to view it.
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 2, p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 300,
              fontSize: '0.75rem',
              fontFamily: 'monospace',
            }}
          >
            {diagram}
          </Box>
        </Box>
      )}

      <Box
        ref={mermaidRef}
        sx={{
          display: isLoading || error ? 'none' : 'block',
          textAlign: 'center',
          '& svg': {
            maxWidth: '100%',
            height: 'auto'
          }
        }}
      />
    </Paper>
  );
};

export default MermaidDiagram;
